//@flow
import type { ID } from '../../../core/types'
import type { ThunkAction } from 'redux-thunk'
import { normalize } from 'normalizr'
import { services } from '../../../core/feathers'
import { batch } from '../../../modules/lib/batchReducer'
import { sadd, srem, sdel } from '../../../modules/set'
import { hmset } from '../../../modules/hash'
import {
  mergeEntities,
  update,
  getById,
  removeOne
} from '../../../modules/entities'
import {
  startCommunication,
  endCommunication,
  isCommInProgress,
  clearCommunication
} from '../../../modules/communication/'
import { getUserCharityId } from './selectors'
import { schemas, domains } from '../../constants'
import {
  CONTACT_FORM_MODAL,
  DELETE_CONTACT_MODAL,
  MYCHARITY_RESOURCE,
  MYCHARITY_AUCTIONS_RESOURCE,
  FETCH_MYCHARITY_REQUEST,
  FETCH_MYCHARITY_SUCCESS,
  FETCH_MYCHARITY_FAILED,
  MY_AUCTIONS_SET,
  FETCH_MYCHARITY_AUCTIONS_REQUEST,
  FETCH_MYCHARITY_AUCTIONS_SUCCESS,
  FETCH_MYCHARITY_AUCTIONS_FAILED,
  UPDATE_CHARITY_RESOURCE,
  UPDATE_CHARITY_REQUEST,
  UPDATE_CHARITY_SUCCESS,
  UPDATE_CHARITY_FAILED,
  STORE_CHARITY
} from './constants'
import union from 'lodash/union'
import without from 'lodash/without'

const storeMyCharity = (myCharity: Object) => {
  const { entities } = normalize(
    myCharity,
    Array.isArray(myCharity) ? [schemas.charities] : schemas.charities
  )
  return mergeEntities(entities)
}

export const addToMyAuctionsSet = (auctions: { [ID]: ID } | { [ID]: ID }[]) => {
  const auctionsIds = [].concat(auctions).map(auction => String(auction.id))
  return sadd(MY_AUCTIONS_SET, auctionsIds)
}

export const removeFromMyAuctionsSet = (
  auctions: { [ID]: ID } | { [ID]: ID }[]
) => {
  const auctionsIds = [].concat(auctions).map(auction => auction.id)
  return srem(MY_AUCTIONS_SET, auctionsIds)
}

export const cleanMyAuctionsSet = sdel(MY_AUCTIONS_SET)

export const clearMyCharityComm = clearCommunication(MYCHARITY_RESOURCE)
export const clearMyCharityAuctionsComm = clearCommunication(
  MYCHARITY_AUCTIONS_RESOURCE
)

export function fetchMyCharity(): ThunkAction {
  return async function(dispatch, getState) {
    const resource = MYCHARITY_RESOURCE
    const myCharityId = getUserCharityId(getState())
    if (isCommInProgress(resource, getState())) {
      return true
    }
    if (!myCharityId) return false
    let myCharity
    try {
      dispatch(batch(FETCH_MYCHARITY_REQUEST, startCommunication(resource)))
      myCharity = await services.charities.get(myCharityId, {
        query: {
          $eager: '[contacts, myAuctions]'
        }
      })
    } catch (error) {
      console.warn('Error fetching charity', myCharityId)
      console.log(error)
      dispatch(batch(FETCH_MYCHARITY_FAILED, endCommunication(resource, false)))
      return false
    }
    dispatch(
      batch(
        FETCH_MYCHARITY_SUCCESS,
        storeMyCharity(myCharity),
        addToMyAuctionsSet(myCharity.myAuctions),
        endCommunication(resource)
      )
    )
    return true
  }
}

export function fetchCharityAuctions(charityId: number): ThunkAction {
  return async function(dispatch, getState) {
    const resource = MYCHARITY_AUCTIONS_RESOURCE
    if (isCommInProgress(resource, getState())) {
      return
    }
    dispatch(
      batch(FETCH_MYCHARITY_AUCTIONS_REQUEST, startCommunication(resource))
    )
    try {
      const data = await services.auctions.find({
        query: {
          charityId,
          $sort: { closeTime: 1 }
        }
      })
      const { entities, result } = normalize(data, [schemas.auctions])
      dispatch(
        batch(
          FETCH_MYCHARITY_AUCTIONS_SUCCESS,
          mergeEntities(entities),
          update(domains.CHARITIES, String(charityId), { auctions: result }),
          endCommunication(resource)
        )
      )
    } catch (error) {
      console.warn('Error fetching my charity auctions')
      console.log(error)
      dispatch(
        batch(
          FETCH_MYCHARITY_AUCTIONS_FAILED,
          endCommunication(resource, false)
        )
      )
    }
  }
}

export const updateCharity = (
  charityId: number | string,
  charityProps: Object
): ThunkAction =>
  async function(dispatch, getState) {
    const resource = UPDATE_CHARITY_RESOURCE
    if (isCommInProgress(resource, getState())) {
      console.log('updateCharity called but another one in progress')
      return false
    }
    dispatch(batch(UPDATE_CHARITY_REQUEST, startCommunication(resource)))
    try {
      const updatedCharity = await services.charities.patch(
        Number(charityId),
        charityProps
      )
      dispatch(
        batch(
          UPDATE_CHARITY_SUCCESS,
          storeCharity(updatedCharity),
          endCommunication(resource)
        )
      )
      return updatedCharity
    } catch (error) {
      console.warn('Patching charity failed', error)
      dispatch(batch(UPDATE_CHARITY_FAILED, endCommunication(resource, false)))
      return false
    }
  }

export function createCharityImage(
  charityId: number,
  { name, type }: { name: string, type: string },
  dataURL: string
) {
  return function(dispatch: Function) {
    const bitmap = dataURL.split(',')[1]
    return services.charities
      .patch(charityId, {
        image: {
          name,
          type,
          bitmap
        }
      })
      .then(res => {
        dispatch(batch(STORE_CHARITY, storeCharity(res)))
      })
      .catch(error => {
        console.warn('Error creating charity image', error)
        return false
      })
  }
}

export function removeCharityImage(charityId: number) {
  return function(dispatch: Function) {
    return services.charities
      .patch(charityId, {
        photo: null,
        photo_thumb: null
      })
      .then(res => {
        dispatch(batch(STORE_CHARITY, storeCharity(res)))
      })
      .catch(error => {
        console.warn('Error removing charity image', error)
        return false
      })
  }
}

export const storeCharity = (data: Array<Object> | Object) => {
  const { entities } = normalize(
    data,
    Array.isArray(data) ? [schemas.charities] : schemas.charities
  )
  return mergeEntities(entities)
}

export function openContactFormModal(contactId: number) {
  return hmset(CONTACT_FORM_MODAL, {
    isModalOpen: true,
    isEditingMode: contactId !== 'new contact',
    contactId
  })
}

export function closeContactFormModal() {
  return hmset(CONTACT_FORM_MODAL, {
    isModalOpen: false,
    isEditingMode: null,
    contactId: null
  })
}

export function openDeleteContactModal(contactId: number) {
  return hmset(DELETE_CONTACT_MODAL, {
    isModalOpen: true,
    contactId
  })
}

export function closeDeleteContactModal() {
  return hmset(DELETE_CONTACT_MODAL, {
    isModalOpen: false,
    contactId: null
  })
}

export const createContact = (charityId: number, charityContact: Object) =>
  async function(dispatch: Function) {
    try {
      const newContact = await services[domains.CONTACTS].create({
        ...charityContact,
        charityId: Number(charityId)
      })
      dispatch(storeContact(newContact))
    } catch (error) {
      console.warn('Error creating contact', error)
      return false
    }
  }

export const updateContact = (contactId: number, contactProps: Object) =>
  async function(dispatch: Function) {
    try {
      const updatedContact = await services[domains.CONTACTS].patch(
        Number(contactId),
        contactProps
      )
      dispatch(storeContact(updatedContact))
    } catch (error) {
      console.warn('Error updating contact', error)
      return false
    }
  }

export const storeContact = (contact: Object) => (
  dispatch: Function,
  getState: Function
) => {
  const { entities } = normalize(
    contact,
    Array.isArray(contact) ? [schemas.contacts] : schemas.contacts
  )
  const state = getState()
  const charity = getById(domains.CHARITIES, contact.charityId, state)
  if (charity) {
    const contacts = union(charity.contacts, [contact.id])
    dispatch(
      batch(
        'STORE_CONTACT',
        mergeEntities(entities),
        update(domains.CHARITIES, charity.id, { contacts })
      )
    )
  }
  return
}

export const deleteContact = (contactId: number) =>
  async function(dispatch: Function) {
    try {
      await services[domains.CONTACTS].remove(Number(contactId))
      dispatch(removeContactFromStore(contactId))
    } catch (error) {
      console.warn('Error deleting contact', error)
      return false
    }
  }

export const removeContactFromStore = (contactId: number) => (
  dispatch: Function,
  getState: Function
) => {
  const state = getState()
  const contact = getById(domains.CONTACTS, contactId, state)
  const charity = getById(domains.CHARITIES, contact.charityId, state)
  if (contact && charity) {
    const updatedContacts = without(charity.contacts, contactId)
    dispatch(
      batch(
        'REMOVE_CONTACT',
        update(domains.CHARITIES, charity.id, { contacts: updatedContacts }),
        removeOne(domains.CONTACTS, String(contactId))
      )
    )
  }
}



// WEBPACK FOOTER //
// ./src/services/mycharity/charity/actions.js